/**
 * Desktop (Web Push) alerts — turning them on for THIS browser.
 *
 * The browser asks the user once (the permission prompt), then hands us a
 * subscription the API pushes to (services/web-push.ts). Alerts then arrive as
 * system notifications even with every Omni tab closed, as long as the browser
 * itself is running — Edge and Chrome keep running in the background on
 * Windows by default.
 *
 * The service worker only exists in a production build (vite-plugin-pwa has
 * devOptions.enabled = false), so on the dev server this reports "unsupported"
 * rather than hanging on navigator.serviceWorker.ready for ever.
 */

import * as api from '@/api/endpoints/notifications.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesToBase64Url(buf) {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

/** The service worker registration, or null if there is none within a few seconds. */
async function registration() {
  if (!pushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), 4000))
  ]);
}

/**
 * 'unsupported' | 'blocked' | 'off' | 'on'
 * 'on' means this browser holds a live subscription; the server is told about
 * it again by syncDesktopPush on every start, so the two cannot drift apart.
 */
export async function desktopPushState() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

async function subscribeWithCurrentKey(reg) {
  const publicKey = await api.fetchPushPublicKey();
  if (!publicKey) throw new Error('The server did not return a push key.');
  let sub = await reg.pushManager.getSubscription();
  // A subscription made against an OLD server key (keys rotated) can never
  // receive anything — replace it rather than re-registering a dead one.
  if (sub) {
    const current = bytesToBase64Url(sub.options?.applicationServerKey);
    if (current && current !== publicKey) {
      await sub.unsubscribe().catch(() => undefined);
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  }
  await api.subscribePush(sub.toJSON());
  return sub;
}

/** Ask permission (must be called from a click) and subscribe. Throws a readable error. */
export async function enableDesktopPush() {
  if (!pushSupported()) throw new Error('This browser does not support desktop alerts.');
  const reg = await registration();
  if (!reg) throw new Error('Desktop alerts need the installed Omni app or a normal (non-private) browser window.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Notifications are blocked for Omni. Allow them in the browser’s site settings (the padlock in the address bar), then try again.'
      : 'Notifications were not allowed.');
  }
  await subscribeWithCurrentKey(reg);
}

export async function disableDesktopPush() {
  const reg = await registration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  await api.unsubscribePush(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/**
 * On start-up: if this browser already allowed alerts, make sure the server
 * still holds the subscription (it may have been pruned, or the key rotated).
 * Silent — never prompts.
 */
export async function syncDesktopPush() {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const reg = await registration();
    if (!reg) return;
    if (!(await reg.pushManager.getSubscription())) return;
    await subscribeWithCurrentKey(reg);
  } catch {
    // A sync failure must not disturb the app; the switch in the panel shows the state.
  }
}
